import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import NotesIcon from '@mui/icons-material/Notes';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import { styled } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';
import { useAdminRole } from '../../../../contexts/AdminRoleContext';

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  maxWidth: '800px',
  margin: '0 auto',
  backgroundColor: '#fff',
}));

const AddMeeting = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedLeadId = searchParams.get('leadId');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Lead selection
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  // Telly employees
  const [tellyList, setTellyList] = useState([]);
  const [tellyLoading, setTellyLoading] = useState(false);

  // BDM employees
  const [bdmList, setBdmList] = useState([]);
  const [bdmLoading, setBdmLoading] = useState(false);

  const { isTelly, isBDM, employeeId, employeeData } = useAdminRole();
  const selfTellyLabel = employeeData?.name ? `Self (You – ${employeeData.name})` : 'Self (You)';
  const showSelfTellyOption = isTelly && employeeData?.id;

  const [formState, setFormState] = useState({
    title: '',
    meeting_date: '',
    meeting_time: '',
    type: '',
    telly_employee_id: null,
    bdm_employee_id: null,
    comment: '',
    presentation_show: false,
  });

  // Fetch leads on mount
  useEffect(() => {
    const fetchLeads = async () => {
      setLeadsLoading(true);
      try {
        const response = await ApiService.get('/admin/leads/get-leads');
        const data = response?.data?.leads || response?.leads || response?.data || [];
        const list = Array.isArray(data) ? data : [];
        setLeads(list);

        // If preselected lead ID, set it
        if (preselectedLeadId) {
          const lead = list.find(l => String(l.id) === String(preselectedLeadId));
          if (lead) {
            setSelectedLead(lead);
          }
        }
      } catch (err) {
        logger.error('Error fetching leads:', err);
        setLeads([]);
      } finally {
        setLeadsLoading(false);
      }
    };
    fetchLeads();
  }, [preselectedLeadId]);

  // Fetch BDM employees on mount
  useEffect(() => {
    const fetchBdmList = async () => {
      // If user IS a BDM, no need to fetch list as it will be self-assigned
      if (isBDM) return;

      setBdmLoading(true);
      try {
        // Using existing endpoint that returns all employees - filtering done on backend or here if needed
        // Assuming there is a get-bdm endpoint effectively or we reuse get-employees
        // Checking existing patterns: AddLead uses /admin/employee/get-employees
        const response = await ApiService.get('/admin/employee/get-employees?pageSize=1000');
        const data = response?.data?.employees || response?.employees || [];
        const list = Array.isArray(data) ? data : [];
        setBdmList(list);
      } catch (err) {
        logger.error('Error fetching BDM list:', err);
        setBdmList([]);
      } finally {
        setBdmLoading(false);
      }
    };
    fetchBdmList();
  }, [isBDM]);

  // Fetch Telly employees on mount
  useEffect(() => {
    const fetchTellyList = async () => {
      setTellyLoading(true);
      try {
        const response = await ApiService.get('/admin/employee/get-telly-employees');
        const data = response?.data?.data || response?.data || response || [];
        const list = Array.isArray(data) ? data : [];
        setTellyList(list);
      } catch (err) {
        logger.error('Error fetching Telly list:', err);
        setTellyList([]);
      } finally {
        setTellyLoading(false);
      }
    };
    fetchTellyList();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!selectedLead) {
        throw new Error('Please select a lead');
      }

      if (!formState.title || !formState.meeting_date) {
        throw new Error('Title and meeting date are required');
      }

      // Logic to determine BDM ID:
      // 1. If manually selected in form (admin), use that.
      // 2. If current user is BDM, use their ID.
      // 3. Fallback to lead's existing assigned BDM (common case when Telly schedules for a BDM).
      let bdmId = formState.bdm_employee_id;

      if (!bdmId) {
        if (isBDM && employeeId) {
          bdmId = employeeId;
        } else if (selectedLead.bdm_employee_id || selectedLead.employee_id) {
          bdmId = selectedLead.bdm_employee_id || selectedLead.employee_id;
        }
      }

      if (!bdmId) {
        throw new Error('No BDM assigned to this meeting. Please assign a BDM.');
      }

      const dateTime = formState.meeting_time
        ? `${formState.meeting_date} ${formState.meeting_time}`
        : `${formState.meeting_date} 10:00`;

      const payload = {
        lead_id: selectedLead.id,
        client_id: selectedLead.user_id || selectedLead.id, // Use lead's user_id as client_id
        employee_id: bdmId,
        telly_employee_id: formState.telly_employee_id || null,
        title: formState.title,
        meeting_date: formState.meeting_date,
        date_time: dateTime,
        type: formState.type || 'General',
        comment: formState.comment || null,
        status: 'scheduled',
        meeting_with: selectedLead.contact_person || 'Client',
        presentation_show: formState.presentation_show,
      };

      await ApiService.post('/admin/meeting/create-meeting', payload);

      setSuccess('Meeting scheduled successfully!');
      setSnackbarOpen(true);

      setTimeout(() => {
        navigate('/admin-dashboard/meetings');
      }, 1500);

    } catch (err) {
      logger.error('Failed to create meeting:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to schedule meeting');
    } finally {
      setLoading(false);
    }
  };

  const getLeadLabel = (lead) => {
    if (!lead) return '';
    const name = lead.contact_person || lead.business_name || '';
    const business = lead.business_name || '';
    const phone = lead.phone_number || '';
    return `${name} ${business ? `(${business})` : ''} ${phone ? `- ${phone}` : ''}`.trim();
  };

  return (
    <Container>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
          }}
        >
          <EventIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Schedule New Meeting
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create a meeting and assign it to the Telly team
          </Typography>
        </Box>
      </Box>

      <FormContainer>
        <form onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Lead Selection */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BusinessIcon sx={{ color: '#6366f1' }} />
            <Typography variant="subtitle1" fontWeight={600}>Select Lead</Typography>
          </Box>
          <Autocomplete
            options={leads}
            loading={leadsLoading}
            value={selectedLead}
            onChange={(event, newValue) => setSelectedLead(newValue)}
            getOptionLabel={getLeadLabel}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Lead"
                placeholder="Search by name, business, or phone"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {leadsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 3 }}
          />

          {selectedLead && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f0fdf4', borderRadius: 2 }}>
              <Typography variant="subtitle2" color="success.dark" fontWeight={600} sx={{ mb: 1 }}>
                Selected Lead Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Contact:</strong> {selectedLead.contact_person || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Business:</strong> {selectedLead.business_name || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> {selectedLead.phone_number || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedLead.email || 'N/A'}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Meeting Details */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <EventIcon sx={{ color: '#6366f1' }} />
            <Typography variant="subtitle1" fontWeight={600}>Meeting Details</Typography>
          </Box>

          <TextField
            fullWidth
            label="Meeting Title"
            name="title"
            value={formState.title}
            onChange={handleChange}
            required
            sx={{ mb: 2 }}
            placeholder="e.g., Initial Consultation, Follow-up Discussion"
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              label="Meeting Date"
              name="meeting_date"
              type="date"
              value={formState.meeting_date}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
            />
            <TextField
              label="Meeting Time"
              name="meeting_time"
              type="time"
              value={formState.meeting_time}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <AccessTimeIcon sx={{ color: '#9ca3af', mr: 1 }} />,
              }}
            />
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Meeting Type</InputLabel>
            <Select
              name="type"
              value={formState.type}
              label="Meeting Type"
              onChange={handleChange}
            >
              <MenuItem value="General">General</MenuItem>
              <MenuItem value="Sales">Sales</MenuItem>
              <MenuItem value="Demo">Demo</MenuItem>
              <MenuItem value="Follow-up">Follow-up</MenuItem>
              <MenuItem value="Onboarding">Onboarding</MenuItem>
              <MenuItem value="Support">Support</MenuItem>
            </Select>
          </FormControl>

          {/* BDM Assignment */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 3 }}>
            <PersonIcon sx={{ color: '#6366f1' }} />
            <Typography variant="subtitle1" fontWeight={600}>Assign BDM</Typography>
          </Box>

          {isBDM ? (
            <TextField
              fullWidth
              label="Assigned BDM"
              value={`Self (You - ${employeeData?.first_name || 'User'})`}
              disabled
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <PersonIcon sx={{ color: '#9ca3af', mr: 1 }} />,
              }}
            />
          ) : (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Assign BDM</InputLabel>
              <Select
                name="bdm_employee_id"
                value={formState.bdm_employee_id || ''}
                label="Assign BDM"
                onChange={handleChange}
                disabled={bdmLoading}
                startAdornment={<PersonIcon sx={{ color: '#9ca3af', mr: 1 }} />}
              >
                <MenuItem value="">
                  <em>{bdmLoading ? 'Loading...' : (bdmList.length === 0 ? 'No BDM available' : 'Select BDM Employee')}</em>
                </MenuItem>
                {bdmList.map((bdm) => (
                  <MenuItem key={bdm.id} value={bdm.id}>
                    {[bdm.first_name, bdm.last_name].filter(Boolean).join(' ') || bdm.email || `Employee #${bdm.id}`}
                  </MenuItem>
                ))}
              </Select>
              {bdmList.length === 0 && !bdmLoading && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                  No active BDM employees found.
                </Typography>
              )}
            </FormControl>
          )}

          {/* Telly Assignment */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 3 }}>
            <PhoneIcon sx={{ color: '#6366f1' }} />
            <Typography variant="subtitle1" fontWeight={600}>Assign Telly Employee</Typography>
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Assign Telly</InputLabel>
            <Select
              name="telly_employee_id"
              value={formState.telly_employee_id || ''}
              label="Assign Telly"
              onChange={handleChange}
              disabled={tellyLoading}
              startAdornment={<PersonIcon sx={{ color: '#9ca3af', mr: 1 }} />}
            >
              <MenuItem value="">
                <em>{tellyLoading ? 'Loading...' : (tellyList.length === 0 ? 'No Telly available' : 'Select Telly Employee')}</em>
              </MenuItem>
              {showSelfTellyOption && (
                <MenuItem key="telly-self" value={employeeData.id}>
                  {selfTellyLabel}
                </MenuItem>
              )}
              {tellyList.map((telly) => (
                <MenuItem key={telly.id} value={telly.id}>
                  {[telly.first_name, telly.last_name].filter(Boolean).join(' ') || telly.email || `Employee #${telly.id}`}
                </MenuItem>
              ))}
            </Select>
            {tellyList.length === 0 && !tellyLoading && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                Add employees with Telly role in Employee Management to assign here
              </Typography>
            )}
          </FormControl>

          {/* Notes */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 3 }}>
            <NotesIcon sx={{ color: '#6366f1' }} />
            <Typography variant="subtitle1" fontWeight={600}>Additional Notes</Typography>
          </Box>

          <TextField
            fullWidth
            label="Meeting Notes/Comments"
            name="comment"
            value={formState.comment}
            onChange={handleChange}
            multiline
            rows={3}
            sx={{ mb: 3 }}
            placeholder="Add any relevant notes about this meeting..."
          />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin-dashboard/meetings')}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !selectedLead}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Schedule Meeting'}
            </Button>
          </Box>
        </form>
      </FormContainer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddMeeting;
